import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, X, Save, Upload, Users, User, Star,
  Image as ImageIcon, Globe, MapPin, Eye, EyeOff, ChevronRight, ArrowLeft,
} from 'lucide-react';
import {
  listPeople, createPerson, updatePerson, deletePerson,
  listPersonBlog, createBlogPost, updateBlogPost, deleteBlogPost,
  listVerticals, uploadFile, resolveFileUrl,
} from '../../services/api';
import { grantBlogAccess, revokeBlogAccess } from '../../services/authorApi';
import { KeyRound, Copy } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import RichTextEditor from '../../components/RichTextEditor';

const CATEGORIES = [
  { key: 'board', label: 'Board of Directors', icon: Star },
  { key: 'management', label: 'Management', icon: Users },
  { key: 'key_personnel', label: 'Key Personnel', icon: User },
];

const emptyPerson = (category = 'board') => ({
  category,
  name: '',
  slug: '',
  designation: '',
  location: '',
  photo_url: '',
  short_desc: '',
  long_desc: '',
  socials: { linkedin: '', twitter: '', facebook: '', instagram: '', youtube: '', email: '', website: '' },
  vertical_slugs: [],
  order: 0,
  active: true,
  is_public: true,
});

const emptyPost = () => ({
  title: '',
  slug: '',
  cover_image: '',
  content_html: '',
  excerpt: '',
  published_at: new Date().toISOString().slice(0, 10),
  active: true,
  is_public: true,
});

const PeopleAdminPage = () => {
  const [tab, setTab] = useState('board');
  const [items, setItems] = useState({ board: [], management: [], key_personnel: [] });
  const [loading, setLoading] = useState(true);
  const [verticals, setVerticals] = useState([]);
  const [drawer, setDrawer] = useState(null); // 'edit-person' | 'blog-list' | 'edit-post'
  const [activePerson, setActivePerson] = useState(null);
  const [form, setForm] = useState(emptyPerson());
  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState(emptyPost());
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef();
  const coverRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPeople();
      const grouped = { board: [], management: [], key_personnel: [] };
      (res.data || []).forEach((p) => {
        grouped[p.category] = grouped[p.category] || [];
        grouped[p.category].push(p);
      });
      setItems(grouped);
    } catch (e) {
      toast({ title: 'Failed to load people', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    listVerticals().then((r) => setVerticals(r.data || [])).catch(() => {});
  }, [load]);

  // ---- Person actions ----
  const openCreate = () => {
    setActivePerson(null);
    setForm({ ...emptyPerson(tab), order: (items[tab]?.length || 0) * 10 + 10 });
    setDrawer('edit-person');
  };
  const openEdit = (p) => {
    setActivePerson(p);
    setForm({
      ...emptyPerson(),
      ...p,
      socials: { linkedin: '', twitter: '', facebook: '', instagram: '', youtube: '', email: '', website: '', ...(p.socials || {}) },
      vertical_slugs: p.vertical_slugs || [],
    });
    setDrawer('edit-person');
  };

  const savePerson = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      if (activePerson) {
        await updatePerson(activePerson.id, form);
        toast({ title: 'Person updated' });
      } else {
        await createPerson(form);
        toast({ title: 'Person added' });
      }
      setDrawer(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally {
      setSaving(false);
    }
  };

  const removePerson = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all their blog posts?`)) return;
    try {
      await deletePerson(p.id);
      load();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, photo_url: res.url }));
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    try {
      const res = await uploadFile(file);
      setPostForm((f) => ({ ...f, cover_image: res.url }));
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  // ---- Blog actions ----
  const openBlogList = async (p) => {
    setActivePerson(p);
    try {
      const res = await listPersonBlog(p.slug);
      setPosts(res.data || []);
      setDrawer('blog-list');
    } catch {
      toast({ title: 'Failed to load blog posts', variant: 'destructive' });
    }
  };

  const openNewPost = () => {
    setEditingPost(null);
    setPostForm(emptyPost());
    setDrawer('edit-post');
  };
  const openEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      ...emptyPost(),
      ...post,
      published_at: (post.published_at || '').slice(0, 10),
    });
    setDrawer('edit-post');
  };

  const savePost = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...postForm };
      if (payload.published_at?.length === 10) {
        payload.published_at = new Date(payload.published_at).toISOString();
      }
      if (editingPost) {
        await updateBlogPost(activePerson.id, editingPost.id, payload);
        toast({ title: 'Post updated' });
      } else {
        await createBlogPost(activePerson.id, payload);
        toast({ title: 'Post published' });
      }
      const res = await listPersonBlog(activePerson.slug);
      setPosts(res.data || []);
      setDrawer('blog-list');
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (post) => {
    if (!window.confirm(`Delete post "${post.title}"?`)) return;
    try {
      await deleteBlogPost(activePerson.id, post.id);
      const res = await listPersonBlog(activePerson.slug);
      setPosts(res.data || []);
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  // -------------------- Render --------------------
  return (
    <div data-testid="admin-people-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CMS</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Leadership & Personnel
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Board, Management and Key Personnel — each with their own profile page and personal blog.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-person-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition"
        >
          <Plus className="w-4 h-4" /> Add person
        </button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#0f5c3d]/20">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setTab(c.key)}
            data-testid={`people-tab-${c.key}`}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === c.key ? 'text-[#0f5c3d]' : 'text-[#0b1a2e]/70 hover:text-[#0b1a2e]'
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <c.icon className="w-4 h-4" /> {c.label}
              <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-black/5">
                {items[c.key]?.length || 0}
              </span>
            </span>
            {tab === c.key && <span className="absolute left-3 right-3 -bottom-[1px] h-0.5 bg-[#0f5c3d] rounded-full" />}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-black/5 p-10 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (items[tab] || []).length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
          <User className="w-10 h-10 mx-auto text-[#0b1a2e]/30 mb-3" />
          <p className="text-[#0b1a2e]/60 mb-4">No {CATEGORIES.find((c) => c.key === tab)?.label.toLowerCase()} added yet.</p>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">
            <Plus className="w-4 h-4" /> Add first person
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items[tab].map((p) => (
            <div key={p.id} data-testid="person-card" className="bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-md transition">
              <div className="aspect-[16/10] bg-[#e2ede6] relative overflow-hidden">
                {p.photo_url ? (
                  <img src={resolveFileUrl(p.photo_url)} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/30">
                    <User className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 text-[10px] font-semibold uppercase tracking-widest">
                  {p.is_public ? <><Eye className="w-3 h-3 text-[#0f5c3d]" /> Public</> : <><EyeOff className="w-3 h-3 text-red-600" /> Internal</>}
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold">{p.designation || '—'}</div>
                <h3 className="mt-1 font-semibold text-[#0b1a2e]">{p.name}</h3>
                {p.location && (
                  <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#0b1a2e]/60">
                    <MapPin className="w-3 h-3" /> {p.location}
                  </div>
                )}
                <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-2">{p.short_desc || '—'}</p>
                <div className="mt-4 flex gap-1.5">
                  <button
                    onClick={async () => {
                      const currentEmail = p.blog_access?.email || '';
                      const email = window.prompt(
                        p.blog_access?.enabled
                          ? `Re-issue blog access for ${p.name}?\nCurrent email: ${currentEmail}\nPress OK to keep, or type a new email:`
                          : `Grant blog access to ${p.name}. Enter their email:`,
                        currentEmail,
                      );
                      if (!email) return;
                      try {
                        const res = await grantBlogAccess(p.id, email.trim());
                        window.prompt(
                          `✅ Blog access granted!\n\nEmail: ${res.email}\nLogin URL: ${res.login_url}\n\nTemporary password (COPY NOW — shown only once):`,
                          res.temp_password,
                        );
                        toast({ title: 'Blog access granted', description: `${p.name} can now sign in at /author/login` });
                        load();
                      } catch (err) {
                        toast({ title: 'Failed', description: err.response?.data?.detail || String(err), variant: 'destructive' });
                      }
                    }}
                    data-testid="grant-blog-access-btn"
                    className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-medium hover:bg-[#f7fbf8] ${p.blog_access?.enabled ? 'border-emerald-500/40 text-emerald-700 bg-emerald-50/40' : 'border-[#0f5c3d]/20'}`}
                    title={p.blog_access?.enabled ? 'Blog access enabled — click to re-issue password' : 'Grant blog access'}
                  >
                    <KeyRound className="w-3.5 h-3.5" /> {p.blog_access?.enabled ? 'Author' : 'Grant blog'}
                  </button>
                  <button onClick={() => openEdit(p)} data-testid="edit-person-btn" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => openBlogList(p)} data-testid="manage-blog-btn" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-[#0f5c3d] text-white text-xs font-medium hover:bg-[#0b1a2e]">
                    Blog <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removePerson(p)} className="inline-flex items-center justify-center px-2 py-1.5 rounded-lg text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Drawer: Edit person ---------------- */}
      {drawer === 'edit-person' && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(null)} />
          <form onSubmit={savePerson} className="relative bg-white w-full sm:max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">
                  {activePerson ? 'Edit person' : 'Add person'}
                </div>
                <div className="text-lg font-semibold text-[#0b1a2e]">{form.name || 'New person'}</div>
              </div>
              <button type="button" onClick={() => setDrawer(null)} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Category</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="person-category" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Display order</span>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Name</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="person-name" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Designation</span>
                  <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Location</span>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Slug (URL)</span>
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto if empty" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs font-mono" />
              </label>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Photo</span>
                <div className="flex items-start gap-3">
                  <div className="w-28 h-28 rounded-xl overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {form.photo_url ? <img src={resolveFileUrl(form.photo_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#0b1a2e]/30" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
                    <button type="button" onClick={() => photoRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-sm hover:bg-[#f7fbf8]"><Upload className="w-4 h-4" /> Upload</button>
                    <input value={form.photo_url || ''} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="or paste URL" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                  </div>
                </div>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Short description</span>
                <textarea rows={2} value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Detailed bio (markdown-lite)</span>
                <textarea rows={6} value={form.long_desc} onChange={(e) => setForm({ ...form, long_desc: e.target.value })} placeholder="## Heading&#10;- Bullet&#10;**bold**" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y font-mono" />
              </label>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-2">Business verticals they lead / are tied to</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border border-[#0f5c3d]/15 rounded-lg">
                  {verticals.map((v) => (
                    <label key={v.slug} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.vertical_slugs.includes(v.slug)}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          vertical_slugs: e.target.checked
                            ? [...f.vertical_slugs, v.slug]
                            : f.vertical_slugs.filter((s) => s !== v.slug),
                        }))}
                        className="accent-[#0f5c3d]"
                      />
                      <span className="text-[#0b1a2e]">{v.title}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-black/5">
                <div className="sm:col-span-2 text-xs text-[#0b1a2e]/60">
                  <b>Social links</b> — paste the full URL, or just the handle (e.g. <code>@handle</code>). We auto-convert handles to the right platform URL.
                </div>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">LinkedIn</span>
                  <input value={form.socials.linkedin || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, linkedin: e.target.value } })} data-testid="person-social-linkedin" placeholder="https://linkedin.com/in/…" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">X (Twitter)</span>
                  <input value={form.socials.twitter || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, twitter: e.target.value } })} data-testid="person-social-twitter" placeholder="https://x.com/handle" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Facebook</span>
                  <input value={form.socials.facebook || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, facebook: e.target.value } })} data-testid="person-social-facebook" placeholder="https://facebook.com/…" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Instagram</span>
                  <input value={form.socials.instagram || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, instagram: e.target.value } })} data-testid="person-social-instagram" placeholder="https://instagram.com/handle" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">YouTube</span>
                  <input value={form.socials.youtube || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, youtube: e.target.value } })} data-testid="person-social-youtube" placeholder="https://youtube.com/@handle" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Email</span>
                  <input value={form.socials.email || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, email: e.target.value } })} data-testid="person-social-email" placeholder="mailto:name@company.com" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Website</span>
                  <input value={form.socials.website || ''} onChange={(e) => setForm({ ...form, socials: { ...form.socials, website: e.target.value } })} data-testid="person-social-website" placeholder="https://" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                </label>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-[#0f5c3d]" data-testid="person-public-toggle" />
                  <Globe className="w-4 h-4 text-[#0f5c3d]" /> Public on website
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#0f5c3d]" />
                  Active
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex items-center justify-end gap-2">
              <button type="button" onClick={() => setDrawer(null)} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
              <button type="submit" disabled={saving} data-testid="save-person-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {activePerson ? 'Save changes' : 'Add person'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------------- Drawer: Blog list ---------------- */}
      {drawer === 'blog-list' && activePerson && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(null)} />
          <div className="relative bg-white w-full sm:max-w-3xl h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={() => setDrawer(null)} className="p-2 hover:bg-black/5 rounded-lg"><ArrowLeft className="w-4 h-4" /></button>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Blog</div>
                  <div className="text-lg font-semibold text-[#0b1a2e] truncate">{activePerson.name}</div>
                </div>
              </div>
              <button onClick={openNewPost} data-testid="new-post-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">
                <Plus className="w-4 h-4" /> New post
              </button>
            </div>
            <div className="p-6 space-y-3 flex-1">
              {posts.length === 0 ? (
                <div className="bg-[#f7fbf8] rounded-xl p-8 text-center text-[#0b1a2e]/60 text-sm">
                  No blog posts yet. Click "New post" to write the first one.
                </div>
              ) : posts.map((post) => (
                <div key={post.id} data-testid="blog-post-item" className="bg-white border border-black/5 rounded-xl p-4 flex items-start gap-4">
                  <div className="w-24 h-16 rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {post.cover_image ? <img src={resolveFileUrl(post.cover_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-[#0b1a2e]/30" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#0b1a2e]/50">{(post.published_at || '').slice(0, 10)}</div>
                    <div className="font-medium text-[#0b1a2e] truncate">{post.title}</div>
                    <div className="text-xs text-[#0b1a2e]/60 line-clamp-1">{post.excerpt || '—'}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditPost(post)} className="p-2 hover:bg-black/5 rounded-lg"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => removePost(post)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Drawer: Edit blog post ---------------- */}
      {drawer === 'edit-post' && activePerson && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer('blog-list')} />
          <form onSubmit={savePost} className="relative bg-white w-full sm:max-w-4xl h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <button type="button" onClick={() => setDrawer('blog-list')} className="p-2 hover:bg-black/5 rounded-lg"><ArrowLeft className="w-4 h-4" /></button>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">
                    {editingPost ? 'Edit post' : 'New post'} · {activePerson.name}
                  </div>
                  <div className="text-lg font-semibold text-[#0b1a2e] truncate">{postForm.title || 'Untitled'}</div>
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="save-post-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingPost ? 'Save' : 'Publish'}
              </button>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Title</span>
                <input required value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} data-testid="post-title" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-lg" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Slug (URL)</span>
                  <input value={postForm.slug} onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })} placeholder="auto if empty" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs font-mono" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Publish date</span>
                  <input type="date" value={postForm.published_at} onChange={(e) => setPostForm({ ...postForm, published_at: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
                </label>
              </div>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Cover image</span>
                <div className="flex items-start gap-3">
                  <div className="w-40 aspect-[16/10] rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {postForm.cover_image ? <img src={resolveFileUrl(postForm.cover_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#0b1a2e]/30" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleCoverUpload(e.target.files?.[0])} />
                    <button type="button" onClick={() => coverRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-sm hover:bg-[#f7fbf8]"><Upload className="w-4 h-4" /> Upload</button>
                    <input value={postForm.cover_image || ''} onChange={(e) => setPostForm({ ...postForm, cover_image: e.target.value })} placeholder="or paste URL" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs" />
                  </div>
                </div>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Excerpt (shown in card previews)</span>
                <textarea rows={2} value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" />
              </label>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-2">Content (rich editor)</span>
                <RichTextEditor
                  value={postForm.content_html}
                  onChange={(html) => setPostForm((f) => ({ ...f, content_html: html }))}
                  placeholder="Start writing your post…"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={postForm.is_public} onChange={(e) => setPostForm({ ...postForm, is_public: e.target.checked })} className="accent-[#0f5c3d]" />
                  <Globe className="w-4 h-4 text-[#0f5c3d]" /> Public
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={postForm.active} onChange={(e) => setPostForm({ ...postForm, active: e.target.checked })} className="accent-[#0f5c3d]" />
                  Active
                </label>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PeopleAdminPage;
